import { useCallback } from 'react'

import {
    getIsMobileAppMode,
    useDataLayerTracking,
    usePostHogTracking,
    ReactNativeMessages,
} from '@taxfix/scouts-helpers'
import { useSingular } from '@scouts/singular'

import { SubscriptionTiers } from './models'
import { GbbPaymentTypes } from './models/payment'

export const TrackingEvent = {
    AccountantProfileModalOpen: 'accountant_profile_open',
    AnnualSelfAssessmentSubscriptionInitiatePayment: 'annual_self_assessment_subscription_initiate_payment',
    AnnualSelfAssessmentSubscriptionPurchase: 'annual_self_assessment_subscription_purchase',
    AnnualSelfAssessmentSubscriptionVisitPaywall: 'annual_self_assessment_subscription_visit_paywall',
    // BundleCreate: 'bundle_create', // @TODO: Needs further refactor of the bundle creation
    CompanySubscriptionInitiatePayment: 'company_subscription_initiate_payment',
    CompanySubscriptionPurchase: 'company_subscription_purchase',
    CompanySubscriptionVisitPaywall: 'company_subscription_visit_paywall',
    BundleInitiatePayment: 'bundle_initiate_payment',
    BundleProcessPayment: 'bundle_process_payment',
    BundlePurchase: 'bundle_purchase',
    BundleVisitPaywall: 'bundle_visit_paywall',
    CompanyReturnInitiatePayment: 'company_return_initiate_payment',
    CompanyReturnPurchase: 'company_return_purchase',
    CompanyReturnStart: 'company_return_start',
    CompanyReturnVisitPaywall: 'company_return_visit_paywall',
    CompanyServiceVisitEligibilitySummary: 'company_service_visit_eligibility_summary',
    ReferralCopyLink: 'referral_copy_link',
    ReferralShareIntent: 'referral_share_intent',
    ReferralInvite: 'referral_invite',
    ReferralPromotionLearnMore: 'referral_promotion_learn_more',
    SelfEmployedSubscriptionInitiatePayment: 'self_employed_subscription_initiate_payment', // @TODO: Add GA4 ecommerce data
    SelfEmployedSubscriptionPurchase: 'self_employed_subscription_purchase', // @TODO: Add GA4 ecommerce data
    SelfEmployedSubscriptionVisitPaywall: 'self_employed_subscription_visit_paywall',
    StickyFeatureFlagSave: 'sticky_feature_flag_save',
    SubscriptionCancelConfirm: 'subscription_cancel_confirm',
    SubscriptionCancelIntent: 'subscription_cancel_intent',
    TaxConsultationCreate: 'tax_consultation_create',
    TaxConsultationInitiatePayment: 'tax_consultation_initiate_payment',
    TaxConsultationPurchase: 'tax_consultation_purchase', // @TODO: Add event for "free" bundle tax consultation "purchase"?
    TaxConsultationVisitPaywall: 'tax_consultation_visit_paywall',
    TaxConsultationPaywallUpsellIntent: 'tax_consultation_paywall_upsell_intent',
    TaxConsultationPaywallUpsellAdd: 'tax_consultation_paywall_upsell_add',
    TaxConsultationPaywallUpsellRemove: 'tax_consultation_paywall_upsell_remove',
    // TaxRegistrationCreate: 'tax_registration_create', // @TODO: Needs further refactor of the tax registration creation
    TaxRegistrationInitiatePayment: 'tax_registration_initiate_payment',
    TaxRegistrationPurchase: 'tax_registration_purchase',
    TaxRegistrationVisitPaywall: 'tax_registration_visit_paywall',
    TaxReturnCreate: 'tax_return_create',
    TaxReturnConfirmReasons: 'tax_return_confirm_reasons',
    TaxReturnInitiatePayment: 'tax_return_initiate_payment',
    TaxReturnProcessPayment: 'tax_return_process_payment',
    TaxReturnPurchase: 'tax_return_purchase', // @TODO: Add event for "free" bundle tax return "purchase"?
    TaxReturnPurchasePayLater: 'tax_return_purchase_pay_later',
    TaxReturnVisitPaywall: 'tax_return_visit_paywall',
    TaxReturnVisitTierSelection: 'tax_return_visit_tier_selection',
    TaxReturnVisitApproval: 'tax_return_visit_approval',
    TaxReturnDownloadTaxReturn: 'tax_return_download',
    TaxReturnApprove: 'tax_return_approve',
    TaxReturnApplyDiscount: 'tax_return_apply_discount',
    TaxReturnPaywallUpsellIntent: 'tax_return_paywall_upsell_intent',
    TaxReturnPaywallUpsellAdd: 'tax_return_paywall_upsell_add',
    TaxReturnPaywallUpsellRemove: 'tax_return_paywall_upsell_remove',
    UserSignedUp: 'user_signed_up',
    PurchaseNew: 'purchase_new',
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
type TrackingEvent = (typeof TrackingEvent)[keyof typeof TrackingEvent]

interface UserProperties {
    cookie_utm_campaign?: string | null
    cookie_utm_content?: string | null
    cookie_utm_medium?: string | null
    cookie_utm_source?: string | null
    cookie_utm_term?: string | null
    initial_variant?: string | null
    referral_source?: string | null
}

interface AllEventProperties {
    amount: number | null
    currency: 'GBP'
    hashed_email: string | null
    is_mobile_app_mode: boolean | null
    is_returning_customer: boolean | null
    purchase_id: number | null
    referral_source: string | null
    variant: string | null
}

interface GlobalEventProperties {
    is_mobile_app_mode: AllEventProperties['is_mobile_app_mode']
}

interface SignupEventProperties {
    hashed_email?: AllEventProperties['hashed_email']
    referral_source: AllEventProperties['referral_source']
    variant: AllEventProperties['variant']
}

interface SyntheticEventProperties {
    is_synthetic?: boolean
}

interface PurchaseEventProperties {
    amount: AllEventProperties['amount']
    is_returning_customer: AllEventProperties['is_returning_customer']
    purchase_id: AllEventProperties['purchase_id']
    is_synthetic?: SyntheticEventProperties['is_synthetic']
    price_shown?: number
    payment_type?: GbbPaymentTypes | null
    tier_type?: SubscriptionTiers | null
}

interface VisitPaywallEventProperties {
    is_upsell_available: boolean
    is_pay_later_available: boolean
    is_returning_customer: boolean
    accountant_id: number | null
    price_shown?: number
    payment_type?: GbbPaymentTypes | null
    tier_type?: SubscriptionTiers | null
}

interface VisitApprovalEventProperties {
    has_payment_on_account: boolean
}

interface ProcessPaymentEventProperties {
    payment_method: string | null
}

interface SubscriptionEventProperties {
    amount?: AllEventProperties['amount']
    purchase_id?: AllEventProperties['purchase_id']
    price_shown?: AllEventProperties['amount']
}

interface SubscriptionCancelEventProperties {
    tier: string
}

interface BundleVisitPaywallEventProperties {
    is_returning_customer: boolean
}
interface CompanyReturnVisitPaywallEventProperties {
    is_returning_customer: boolean
}
interface ReferralCopyLinkEventProperties {
    location: 'input' | 'button'
}
interface ReferralShareIntentEventProperties {
    share_type: 'email' | 'whatsapp' | 'facebook' | 'twitter' | 'native' | 'sms' | 'messenger'
}
interface ReferralInviteEventProperties {
    invite_type: 'email'
    invite_count: number
}
interface ReferralPromotionLearnMoreEventProperties {
    referral_promotion_location: 'header' | 'dashboard' | 'summary' | 'user_menu'
}

interface StickyFeatureFlagEventProperties {
    feature_flag: string
    $set_once: Record<string, unknown>
}

interface TaxConsultationCreateEventProperties {
    is_returning_customer: boolean
}
interface TaxConsultationVisitPaywallEventProperties {
    is_returning_customer: boolean
    is_upsell_available: boolean
}
interface TaxReturnCreateEventProperties {
    is_returning_customer: boolean
    is_autocreate?: boolean
}

interface TaxReturnConfirmReasonsEventProperties {
    is_returning_customer: boolean
    has_requested_phone_call: boolean
    reasons: string[]
}

interface TaxReturnApplyDiscountEventProperties {
    discount_code: string
}

interface TaxReturnVisitTierSelectionEventProperties {
    is_returning_customer: boolean
}

interface CompanyServiceVisitEligibilitySummaryEventProperties {
    is_eligible_for_subscription: boolean
}

interface AnnualSelfAssessmentSubscriptionVisitPaywallEventProperties {
    price_shown: number
    price_shown_subscription: number
    price_shown_one_off: number
}

type EventProperties = {
    [TrackingEvent.AccountantProfileModalOpen]: { accountantId: number }
    // [TrackingEvent.BundleCreate]: undefined -- @TODO: Needs further refactor of the bundle creation to implement
    [TrackingEvent.AnnualSelfAssessmentSubscriptionInitiatePayment]: SubscriptionEventProperties
    [TrackingEvent.AnnualSelfAssessmentSubscriptionPurchase]: PurchaseEventProperties
    [TrackingEvent.AnnualSelfAssessmentSubscriptionVisitPaywall]: AnnualSelfAssessmentSubscriptionVisitPaywallEventProperties
    [TrackingEvent.BundleInitiatePayment]: PurchaseEventProperties
    [TrackingEvent.BundlePurchase]: PurchaseEventProperties // @TODO: Differentiate direct and upsold bundles
    [TrackingEvent.BundleVisitPaywall]: BundleVisitPaywallEventProperties
    [TrackingEvent.CompanyReturnStart]: undefined
    [TrackingEvent.CompanyReturnVisitPaywall]: CompanyReturnVisitPaywallEventProperties
    [TrackingEvent.CompanyReturnInitiatePayment]: PurchaseEventProperties
    [TrackingEvent.CompanyReturnPurchase]: PurchaseEventProperties
    [TrackingEvent.CompanyServiceVisitEligibilitySummary]: CompanyServiceVisitEligibilitySummaryEventProperties
    [TrackingEvent.CompanySubscriptionInitiatePayment]: SubscriptionEventProperties
    [TrackingEvent.CompanySubscriptionPurchase]: PurchaseEventProperties
    [TrackingEvent.CompanySubscriptionVisitPaywall]: undefined
    [TrackingEvent.ReferralCopyLink]: ReferralCopyLinkEventProperties
    [TrackingEvent.ReferralShareIntent]: ReferralShareIntentEventProperties
    [TrackingEvent.ReferralInvite]: ReferralInviteEventProperties
    [TrackingEvent.ReferralPromotionLearnMore]: ReferralPromotionLearnMoreEventProperties
    [TrackingEvent.SelfEmployedSubscriptionInitiatePayment]: undefined
    [TrackingEvent.SelfEmployedSubscriptionPurchase]: PurchaseEventProperties
    [TrackingEvent.SelfEmployedSubscriptionVisitPaywall]: undefined
    [TrackingEvent.StickyFeatureFlagSave]: StickyFeatureFlagEventProperties
    [TrackingEvent.SubscriptionCancelConfirm]: SubscriptionCancelEventProperties
    [TrackingEvent.SubscriptionCancelIntent]: SubscriptionCancelEventProperties
    [TrackingEvent.TaxConsultationCreate]: TaxConsultationCreateEventProperties
    [TrackingEvent.TaxConsultationInitiatePayment]: PurchaseEventProperties
    [TrackingEvent.TaxConsultationPurchase]: PurchaseEventProperties // @TODO: Differentiate personal and company tax consultations
    [TrackingEvent.TaxConsultationVisitPaywall]: TaxConsultationVisitPaywallEventProperties
    [TrackingEvent.TaxConsultationPaywallUpsellIntent]: undefined
    [TrackingEvent.TaxConsultationPaywallUpsellAdd]: undefined
    [TrackingEvent.TaxConsultationPaywallUpsellRemove]: undefined
    [TrackingEvent.TaxRegistrationInitiatePayment]: PurchaseEventProperties
    [TrackingEvent.TaxRegistrationPurchase]: PurchaseEventProperties
    [TrackingEvent.TaxRegistrationVisitPaywall]: undefined
    [TrackingEvent.TaxReturnConfirmReasons]: TaxReturnConfirmReasonsEventProperties
    [TrackingEvent.TaxReturnCreate]: TaxReturnCreateEventProperties
    [TrackingEvent.TaxReturnInitiatePayment]: PurchaseEventProperties
    [TrackingEvent.TaxReturnProcessPayment]: ProcessPaymentEventProperties
    [TrackingEvent.BundleProcessPayment]: ProcessPaymentEventProperties
    [TrackingEvent.TaxReturnPurchase]: PurchaseEventProperties
    [TrackingEvent.TaxReturnPurchasePayLater]: PurchaseEventProperties
    [TrackingEvent.TaxReturnVisitApproval]: VisitApprovalEventProperties
    [TrackingEvent.TaxReturnVisitPaywall]: VisitPaywallEventProperties
    [TrackingEvent.TaxReturnVisitTierSelection]: TaxReturnVisitTierSelectionEventProperties
    [TrackingEvent.TaxReturnDownloadTaxReturn]: undefined
    [TrackingEvent.TaxReturnApprove]: undefined
    [TrackingEvent.TaxReturnApplyDiscount]: TaxReturnApplyDiscountEventProperties
    [TrackingEvent.UserSignedUp]: SignupEventProperties
    [TrackingEvent.PurchaseNew]: PurchaseEventProperties
    [TrackingEvent.TaxReturnPaywallUpsellIntent]: undefined
    [TrackingEvent.TaxReturnPaywallUpsellAdd]: undefined
    [TrackingEvent.TaxReturnPaywallUpsellRemove]: undefined
}

const PURCHASE_EVENTS = new Set<TrackingEvent>([
    TrackingEvent.BundlePurchase,
    TrackingEvent.CompanyReturnPurchase,
    TrackingEvent.CompanySubscriptionPurchase,
    TrackingEvent.SelfEmployedSubscriptionPurchase,
    TrackingEvent.TaxConsultationPurchase,
    TrackingEvent.TaxRegistrationPurchase,
    TrackingEvent.TaxReturnPurchase,
    TrackingEvent.TaxReturnPurchasePayLater,
    TrackingEvent.AnnualSelfAssessmentSubscriptionPurchase,
])

// Check if an event is a purchase event
const isPurchaseEvent = (name: TrackingEvent): name is TrackingEvent => {
    return PURCHASE_EVENTS.has(name)
}

type Capture = {
    <T extends TrackingEvent>(
        name: T,
        properties: EventProperties[T],
        dataLayerOnlyProperties?: Record<string, unknown>
    ): void
    <T extends TrackingEvent>(name: T): void
}

export const getGlobalEventProperties = () => ({
    is_mobile_app_mode: getIsMobileAppMode(),
})

export const useEventTracking = () => {
    const {
        captureEvent: postHogCaptureEvent,
        identifyUser: postHogIdentifyUser,
        reset: postHogResetUser,
        capturePageView: postHogCapturePageView,
    } = usePostHogTracking<TrackingEvent, UserProperties, GlobalEventProperties>()
    const { captureEvent: dataLayerCaptureEvent, identifyUser: dataLayerIdentifyUser } =
        useDataLayerTracking<TrackingEvent>()
    const { track: trackSingularEvent } = useSingular()

    const identify = (id: number, properties: UserProperties) => {
        postHogIdentifyUser(id, properties)
        dataLayerIdentifyUser(id)
    }

    const capturePageView = () => {
        postHogCapturePageView(getGlobalEventProperties())
    }

    const capture: Capture = useCallback(
        <T extends TrackingEvent>(
            name: T,
            properties?: EventProperties[T],
            dataLayerOnlyProperties?: Record<string, unknown>
        ) => {
            /**
             * Do the actual sending of the events to all possible destinations.
             */
            const sendEvent = <T extends TrackingEvent>(
                name: T,
                properties?: EventProperties[T],
                dataLayerOnlyProperties?: Record<string, unknown>
            ) => {
                postHogCaptureEvent(name, { ...properties, ...getGlobalEventProperties() })
                dataLayerCaptureEvent(name, {
                    ...properties,
                    ...dataLayerOnlyProperties,
                    ...getGlobalEventProperties(),
                })

                ReactNativeMessages.trackingEvent({ name, properties: { ...properties } })

                trackSingularEvent(name, properties)
            }

            /**
             * A synthetic event is an event that is derived from another events based on certain conditions related to the payload
             * of the original event. It should be tracked as a separate event and sent to all destinations as if it were a regular
             * event.
             */
            const captureSyntheticEvent = <T extends TrackingEvent>(
                name: T,
                properties?: EventProperties[T],
                dataLayerOnlyProperties?: Record<string, unknown>
            ) => {
                // This is so that we cut any potential recursive calls to this function.
                // For now, it's not allowed to generate a synthetic event from another synthetic event.
                if (!isPurchaseEvent(name) || (properties as PurchaseEventProperties)?.is_synthetic) return

                // Mapping rules for "purchase_new" event
                if (
                    // It's a purchase event with the correct properties type
                    isPurchaseEvent(name) &&
                    // It's not a returning customer
                    (properties as PurchaseEventProperties)?.is_returning_customer === false
                ) {
                    sendEvent(
                        TrackingEvent.PurchaseNew,
                        {
                            ...(properties as PurchaseEventProperties),
                            is_synthetic: true,
                        },
                        dataLayerOnlyProperties
                    )
                }
            }

            sendEvent(name, properties, dataLayerOnlyProperties)
            captureSyntheticEvent(name, properties, dataLayerOnlyProperties)
        },
        [postHogCaptureEvent, dataLayerCaptureEvent, trackSingularEvent]
    )

    return { capture, capturePageView, identify, unidentify: postHogResetUser }
}
